'use client';

import { captureException } from '@sentry/nextjs';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import type { ErrorBoundaryProps, ErrorBoundaryState } from './ErrorBoundary.type';

/**
 * Reusable error boundary component that catches JavaScript errors in its child tree,
 * reports them to Sentry, optionally calls onError, and displays a fallback UI instead of crashing.
 *
 * Must be a class component—React does not yet provide a hooks-based API for error boundaries.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureException(error, {
      level: 'error',
      tags: { context: 'ErrorBoundary' },
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
    this.props.onError?.(error, errorInfo);
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      if (typeof fallback === 'function') {
        return fallback(error, this.reset);
      }
      if (fallback) {
        return fallback;
      }
      return null;
    }

    return children;
  }
}
